import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import styles from './styles.module.css';
import { MdClose } from 'react-icons/md';
import {
  LuSparkles,
  LuPenLine,
  LuCalendarDays,
} from 'react-icons/lu';
import {
  FaFacebook,
  FaInstagram,
  FaLinkedinIn,
  FaTiktok,
} from 'react-icons/fa';
import { XTwitter } from '../../../assets/svgs/icons';
import { normalizeNetworkList } from '../utils/socialNetworkMapper';
import { AttachImageButton } from '../AttachImageButton';
import { AttachReferenceArt } from '../../../components/AttachReferenceArt';
import { PublicationStatusBadge } from '../PublicationStatusBadge';
import {
  resolveSessionUpdatedAtToken,
  withArtDisplayCacheBust,
} from '../utils/artDisplayCacheBust';

const EMPTY_ATTACHED_IMAGES = [];
const EMPTY_SCHEDULE_DATA = {};

export const PreviewArt = ({
  images = [],
  totalImages = 0,
  /** Incrementado no pai após edição — força novo <img> e evita cache com mesma URL. */
  imageRenderKey = 0,
  sessionDetail = null,
  selectedImage,
  onSelectImage,
  onClose,
  onEditRequest,
  isEditing = false,
  editingPieceNumbers = [],
  defaultTab = 'ai',
  isCalendarTabEnabled = false,
  isScheduleReadOnly = false,
  scheduleData = EMPTY_SCHEDULE_DATA,
  scheduleSyncWarning = false,
  onScheduleDataChange,
  attachedImages: attachedImagesProp = EMPTY_ATTACHED_IMAGES,
  onAttachImages,
  onOpenProductImageSelector,
  isApproved = false,
  onTabChange,
  canUseAI = false,
}) => {
  const { t } = useTranslation();

  const getPieceNumber = (art) => {
    if (!art || typeof art !== 'object') {
      return null;
    }
    const pieceNumber = art.pieceNumber ?? art.piece_number;
    return typeof pieceNumber === 'number' ? pieceNumber : null;
  };

  const isPieceEditing = (pieceNumber) => {
    if (pieceNumber == null) {
      return Boolean(isEditing);
    }
    if (Array.isArray(editingPieceNumbers) && editingPieceNumbers.length > 0) {
      return editingPieceNumbers.includes(pieceNumber);
    }
    return Boolean(isEditing);
  };

  const selectedPieceNumber = getPieceNumber(selectedImage);
  const isSelectedImageEditing = isPieceEditing(selectedPieceNumber);

  const [editText, setEditText] = useState('');
  const [activeTab, setActiveTab] = useState(defaultTab);
  const [attachedImages, setAttachedImages] = useState(attachedImagesProp);
  const [attachArtOpen, setAttachArtOpen] = useState(false);
  const [localScheduleData, setLocalScheduleData] = useState({
    caption: '',
    publishDate: '',
    publishTime: '',
    selectedNetworks: [],
    publicationStatus: '',
    ...scheduleData,
  });

  const resolvedScheduleData = { ...localScheduleData, ...scheduleData };

  useEffect(() => {
    setLocalScheduleData((prev) => ({ ...prev, ...scheduleData }));
  }, [scheduleData]);

  const skeletonCount = Math.max(0, totalImages - images.length);
  const sessionUpdatedAt = resolveSessionUpdatedAtToken(sessionDetail);
  const cacheBustOptions = (revision = null) => ({
    renderKey: imageRenderKey,
    revision,
    sessionUpdatedAt,
  });

  useEffect(() => {
    if (defaultTab === 'edit' && !isCalendarTabEnabled) {
      setActiveTab('ai');
      return;
    }

    if (isApproved && defaultTab === 'ai') {
      setActiveTab(isCalendarTabEnabled ? 'edit' : 'ai');
      return;
    }

    setActiveTab(defaultTab);
  }, [defaultTab, isCalendarTabEnabled, isApproved]);

  useEffect(() => {
    if ((isApproved || !canUseAI) && activeTab === 'ai') {
      if (isCalendarTabEnabled) {
        setActiveTab('edit');
      }
    }
  }, [isApproved, canUseAI, activeTab, isCalendarTabEnabled]);

  useEffect(() => {
    setAttachedImages(attachedImagesProp);
  }, [attachedImagesProp]);

  useEffect(() => {
    if (!isCalendarTabEnabled && activeTab === 'edit') {
      setActiveTab('ai');
    }
  }, [activeTab, isCalendarTabEnabled]);

  useEffect(() => {
    if (onTabChange) {
      onTabChange(activeTab);
    }
  }, [activeTab, onTabChange]);


  const resolveSelectedSrc = () => {
    if (!selectedImage) return null;
    if (typeof selectedImage === 'string') return selectedImage;
    return (
      selectedImage.url ||
      selectedImage.imageUrl ||
      null
    );
  };

  const resolveMainImgSrcForDisplay = () => {
    const raw = resolveSelectedSrc();
    const revision =
      typeof selectedImage === 'object' && selectedImage
        ? selectedImage.revision
        : null;
    if (typeof raw === 'string' && raw) {
      return withArtDisplayCacheBust(raw, cacheBustOptions(revision));
    }
    if (typeof selectedImage === 'string') {
      return withArtDisplayCacheBust(
        selectedImage,
        cacheBustOptions(revision),
      );
    }
    return '';
  };

  const thumbSrcForDisplay = (image) => {
    const raw =
      typeof (image.url || image.imageUrl || image) === 'string'
        ? image.url || image.imageUrl || image
        : '';
    return withArtDisplayCacheBust(raw, cacheBustOptions(image?.revision));
  };

  const handleSendEdit = () => {
    if (!editText.trim() || isSelectedImageEditing) return;

    const baseImage = resolveSelectedSrc() || selectedImage;
    const attachedUrls = attachedImages
      .map((img) => getImageUrl(img))
      .filter(Boolean);
    const imageUrls = [baseImage, ...attachedUrls];

    onEditRequest && onEditRequest(selectedImage, editText.trim(), imageUrls);
    setEditText('');
    setAttachedImages([]);
    if (onAttachImages) {
      onAttachImages([]);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendEdit();
    }
  };

  const handleScheduleChange = (field, value) => {
    if (isScheduleReadOnly) return;

    const updated = { ...resolvedScheduleData, [field]: value };
    setLocalScheduleData(updated);
    onScheduleDataChange && onScheduleDataChange(field, value);
  };

  const handleDateChange = (e) => {
    let value = e.target.value.replace(/\D/g, '');
    if (value.length > 8) value = value.slice(0, 8);
    if (value.length > 2) {
      value = `${value.slice(0, 2)}/${value.slice(2)}`;
    }
    if (value.length > 5) {
      value = `${value.slice(0, 5)}/${value.slice(5)}`;
    }
    handleScheduleChange('publishDate', value);
  };

  const handleTimeChange = (e) => {
    let value = e.target.value.replace(/\D/g, '');
    if (value.length > 4) value = value.slice(0, 4);
    if (value.length > 2) {
      value = `${value.slice(0, 2)}:${value.slice(2)}`;
    }
    handleScheduleChange('publishTime', value);
  };

  const getImageUrl = (image) => {
    if (!image) return null;
    if (image.fileDataBase64) return image.fileDataBase64;
    if (image.image) return image.image;
    if (image.base64) return image.base64;
    if (image.url) return image.url;
    if (image.imageUrl) return image.imageUrl;
    if (typeof image === 'string') return image;
    return null;
  };

  const handleAttachImages = (imageOrImages) => {
    const newImages = Array.isArray(imageOrImages)
      ? imageOrImages
      : [imageOrImages];
    const updated = [...attachedImages, ...newImages];
    setAttachedImages(updated);
    if (onAttachImages) {
      onAttachImages(updated);
    }
  };

  const handleRemoveAttachedImage = (indexToRemove) => {
    const updated = attachedImages.filter(
      (_, index) => index !== indexToRemove,
    );
    setAttachedImages(updated);
    if (onAttachImages) {
      onAttachImages(updated);
    }
  };

  const socialNetworks = [
    { id: 'facebook', Icon: FaFacebook },
    { id: 'instagram', Icon: FaInstagram },
    { id: 'linkedin', Icon: FaLinkedinIn },
    { id: 'tiktok', Icon: FaTiktok },
    { id: 'twitter', Icon: null },
  ];

  const selectedNetworks = normalizeNetworkList(
    resolvedScheduleData.selectedNetworks || [],
  );
  const caption = resolvedScheduleData.caption || '';
  const publishDate = resolvedScheduleData.publishDate || '';
  const publishTime = resolvedScheduleData.publishTime || '';

  return (
    <div className={styles.previewContainer}>
      <div
        style={{
          maxWidth: '100%',
          width: '100%',
          height: '100%',
          maxHeight: '100%',
          display: 'flex',
          flexDirection: 'column',
          gap: '16px',
          overflow: 'auto',
          justifyContent: 'flex-start',
        }}
      >
        {/* Thumbnail carousel — visible only in AI mode */}
        {activeTab === 'ai' && (
          <div className={styles.thumbnailCarousel}>
            {Array.from({ length: totalImages }, (_, index) => {
              const pieceNumber = index + 1;
              const art = images.find((img) => getPieceNumber(img) === pieceNumber);
              const thumbIsEditing = art ? isPieceEditing(pieceNumber) : false;
              const isSelected = art && (art.url || art.imageUrl || art) === (resolveSelectedSrc() || selectedImage);

              if (art) {
                return (
                  <div
                    key={`thumb-${pieceNumber}-${imageRenderKey}`}
                    className={`${styles.thumbnailItem} ${isSelected ? styles.thumbnailActive : ''} ${thumbIsEditing ? styles.thumbnailItemEditing : ''}`}
                    onClick={() => onSelectImage && onSelectImage(art)}
                  >
                    <img
                      key={`thumb-img-${pieceNumber}-${imageRenderKey}-${art.revision ?? ''}-${art.url || art.imageUrl || ''}`}
                      src={thumbSrcForDisplay(art)}
                      alt={art.title || t('chatsocket.preview_art.art_fallback', { index: pieceNumber })}
                      className={thumbIsEditing ? styles.thumbnailImageEditing : ''}
                    />
                    {thumbIsEditing && (
                      <div className={styles.thumbnailEditingOverlay}>
                        <div className={styles.editingSpinner} />
                      </div>
                    )}
                  </div>
                );
              }

              return (
                <div
                  key={`skeleton-${pieceNumber}-${imageRenderKey}`}
                  className={styles.thumbnailSkeleton}
                >
                  <div className={styles.thumbnailSkeletonInner} />
                </div>
              );
            })}
          </div>
        )}

        {/* Main image + close + floating buttons */}
        <div className={styles.mainImageRow}>
          <div
            style={{
              display: 'flex',
              height: '100%',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <button
              className={styles.closeButton}
              onClick={onClose}
              title={t('chatsocket.preview_art.close')}
            >
              <MdClose size={18} />
            </button>
          </div>

          <div className={styles.mainImageWrapper}>
            <img
              key={`${resolveSelectedSrc() || 'preview-art'}-${imageRenderKey}-${selectedImage?.revision ?? ''}`}
              src={resolveMainImgSrcForDisplay()}
              alt={t('chatsocket.preview_art.preview_alt')}
              className={`${styles.previewImage} ${isSelectedImageEditing ? styles.previewImageEditing : ''}`}
            />
            {selectedImage?.publicationStatus && (
              <div className={styles.statusOverlay}>
                <PublicationStatusBadge
                  status={selectedImage.publicationStatus}
                />
              </div>
            )}
            {isSelectedImageEditing && (
              <div className={styles.editingOverlay}>
                <div className={styles.editingSpinner} />
                <span className={styles.editingText}>{t('chatsocket.preview_art.editing')}</span>
              </div>
            )}
          </div>

          <div
            style={{ height: '100%', display: 'flex', alignItems: 'center' }}
          >
            <div className={styles.floatingButtonsPill}>
              <button
                className={`${styles.floatingButtonSecondary} ${activeTab === 'ai' ? styles.floatingButtonActive : ''} ${(isApproved || !canUseAI) ? styles.floatingButtonDisabled : ''}`}
                title={isApproved ? t('chatsocket.preview_art.editing_disabled') : !canUseAI ? t('chatsocket.preview_art.ai_unavailable') : t('chatsocket.preview_art.ai_default')}
                onClick={() => {
                  if (!isApproved && canUseAI) {
                    setActiveTab('ai');
                  }
                }}
                disabled={isApproved || !canUseAI}
              >
                <LuPenLine size={20} />
              </button>
              <button
                className={`${styles.floatingButtonSecondary} ${activeTab === 'edit' && isCalendarTabEnabled ? styles.floatingButtonActive : ''} ${!isCalendarTabEnabled ? styles.floatingButtonDisabled : ''}`}
                title={
                  isCalendarTabEnabled
                    ? t('chatsocket.preview_art.schedule_tooltip')
                    : t('chatsocket.preview_art.schedule_disabled')
                }
                onClick={() => {
                  if (isCalendarTabEnabled) {
                    setActiveTab('edit');
                  }
                }}
                disabled={!isCalendarTabEnabled}
              >
                <LuCalendarDays size={24} />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* AI edit input */}
      {/* {activeTab === 'ai' && (
        <>
          {attachedImages.length > 0 && (
            <div className={styles.imagesPreview}>
              {attachedImages.map((img, index) => (
                <div key={index} className={styles.imagePreviewItem}>
                  <img
                    src={getImageUrl(img)}
                    alt="Preview"
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                    }}
                  />
                  <button
                    type="button"
                    className={styles.removeImageButton}
                    onClick={() => handleRemoveAttachedImage(index)}
                    title="Remover imagem"
                  >
                    <svg
                      viewBox="0 0 24 24"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M18 6L6 18M6 6L18 18"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </button>
                </div>
              ))}
            </div>
          )}

          <div className={styles.actionsContainer}>
            <div
              className={`${styles.editInputContainer} ${isEditing ? styles.editInputContainerDisabled : ''}`}
            >
              <div className={styles.inputArea}>
                <input
                  type="text"
                  className={styles.editInput}
                      placeholder={
                        isEditing
                          ? t('chatsocket.chat.wait_edit')
                          : t('chatsocket.chat.placeholder_art_edit')
                      }
                  value={editText}
                  onChange={(e) => setEditText(e.target.value)}
                  onKeyDown={handleKeyDown}
                  disabled={isEditing}
                />
              </div>

              <div className={styles.actionsRow}>
                <div className={styles.attachSection}>
                  <div style={{ position: 'relative' }}>
                    <AttachImageButton
                      visible={true}
                      onClick={() => setAttachArtOpen(!attachArtOpen)}
                    />
                    <AttachReferenceArt
                      open={attachArtOpen}
                      setOpen={setAttachArtOpen}
                      onImageSelect={handleAttachImages}
                      verticalLayout={true}
                      hideExpandButton={true}
                      menuPosition="top"
                      menuClassName={styles.attachMenuOffset}
                      options={[
                        {
                          id: 'type',
                          label: t('chatsocket.buttons.images'),
                          openModalOnly: true,
                          onSelect: () => {
                            if (onOpenProductImageSelector) {
                              onOpenProductImageSelector();
                            }
                          },
                        },
                      ]}
                      acceptedFormats="image/*"
                      maxFileSize={10 * 1024 * 1024}
                      showPreview={false}
                    />
                  </div>
                </div>

                <button
                  className={`${styles.sendButton} ${isEditing ? styles.sendButtonDisabled : ''}`}
                  title={t('chatsocket.preview_art.send')}
                  onClick={handleSendEdit}
                  disabled={isEditing}
                >
                  <svg
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M20.2346 5.68597C20.6666 4.49097 19.5086 3.33297 18.3136 3.76597L3.70855 9.04797C2.50955 9.48197 2.36455 11.118 3.46755 11.757L8.12955 14.456L12.2926 10.293C12.4812 10.1108 12.7338 10.01 12.996 10.0123C13.2582 10.0146 13.509 10.1197 13.6944 10.3051C13.8798 10.4906 13.9849 10.7414 13.9872 11.0036C13.9895 11.2658 13.8887 11.5184 13.7066 11.707L9.54355 15.87L12.2436 20.532C12.8816 21.635 14.5176 21.489 14.9516 20.291L20.2346 5.68597Z"
                      fill="white"
                    />
                  </svg>
                </button>
              </div>
            </div>
          </div>
        </>
      )} */}

      {/* Detail panel: caption, date, social networks */}
      {activeTab === 'edit' && (
        <div className={styles.detailsContainer}>
          {scheduleSyncWarning && (
            <p className={styles.scheduleSyncWarning} role="status">
              {t('chatsocket.preview_art.schedule_sync_warning')}
            </p>
          )}
          {/* Publication caption */}
          <div className={styles.detailsSection}>
            <span className={styles.detailsLabel}>{t('chatsocket.preview_art.caption_label')}</span>
            <div className={styles.captionInputContainer}>
              <textarea
                className={styles.captionInput}
                placeholder={t('chatsocket.preview_art.caption_placeholder')}
                value={caption}
                onChange={(e) =>
                  handleScheduleChange('caption', e.target.value)
                }
                rows={3}
                disabled={isScheduleReadOnly}
              />
              <button
                className={styles.captionSparkleButton}
                title={t('chatsocket.preview_art.generate_ai')}
                disabled={isScheduleReadOnly}
              >
                <LuSparkles size={20} />
              </button>
            </div>
          </div>

          {/* Date/Time + Social networks */}
          <div className={styles.detailsBottomRow}>
            {/* Publish date */}
            <div className={styles.detailsSection}>
              <span className={styles.detailsLabel}>{t('chatsocket.preview_art.date_label')}</span>
              <div className={styles.dateTimeRow}>
                <div className={styles.dateTimeField}>
                  <LuCalendarDays size={24} color="#9002CD" />
                  <input
                    type="text"
                    className={styles.dateTimeInput}
                    placeholder={t('chatsocket.preview_art.date_placeholder')}
                    value={publishDate}
                    onChange={handleDateChange}
                    maxLength={10}
                    disabled={isScheduleReadOnly}
                  />
                </div>
                <div className={styles.dateTimeField}>
                  <svg
                    width="24"
                    height="24"
                    viewBox="0 0 20 20"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M15 1.33989C16.5083 2.21075 17.7629 3.46042 18.6398 4.96519C19.5167 6.46997 19.9854 8.17766 19.9994 9.91923C20.0135 11.6608 19.5725 13.3758 18.72 14.8946C17.8676 16.4133 16.6332 17.6831 15.1392 18.5782C13.6452 19.4733 11.9434 19.9627 10.2021 19.998C8.46083 20.0332 6.74055 19.6131 5.21155 18.7791C3.68256 17.9452 2.39787 16.7264 1.48467 15.2434C0.571462 13.7604 0.0614093 12.0646 0.00500011 10.3239L0 9.99989L0.00500011 9.67589C0.0610032 7.94888 0.563548 6.26585 1.46364 4.79089C2.36373 3.31592 3.63065 2.09934 5.14089 1.25977C6.65113 0.420205 8.35315 -0.0137108 10.081 0.000330246C11.8089 0.0143713 13.5036 0.47589 15 1.33989ZM10 3.99989C9.75507 3.99992 9.51866 4.08985 9.33563 4.25261C9.15259 4.41537 9.03566 4.63964 9.007 4.88289L9 4.99989V9.99989L9.009 10.1309C9.0318 10.3044 9.09973 10.4689 9.206 10.6079L9.293 10.7079L12.293 13.7079L12.387 13.7899C12.5624 13.926 12.778 13.9998 13 13.9998C13.222 13.9998 13.4376 13.926 13.613 13.7899L13.707 13.7069L13.79 13.6129C13.9261 13.4375 13.9999 13.2219 13.9999 12.9999C13.9999 12.7779 13.9261 12.5623 13.79 12.3869L13.707 12.2929L11 9.58489V4.99989L10.993 4.88289C10.9643 4.63964 10.8474 4.41537 10.6644 4.25261C10.4813 4.08985 10.2449 3.99992 10 3.99989Z"
                      fill="#9002CD"
                    />
                  </svg>
                  <input
                    type="text"
                    className={styles.dateTimeInput}
                    placeholder={t('chatsocket.preview_art.time_placeholder')}
                    value={publishTime}
                    onChange={handleTimeChange}
                    maxLength={5}
                    disabled={isScheduleReadOnly}
                  />
                </div>
              </div>
            </div>

            {/* Social networks */}
            <div className={styles.detailsSection}>
              <span className={styles.detailsLabel}>{t('chatsocket.preview_art.networks_label')}</span>
              <div className={styles.socialRow}>
                {socialNetworks
                  .filter(({ id }) => selectedNetworks.includes(id))
                  .map(({ id, Icon }) => (
                    <button
                      key={id}
                      className={`${styles.socialButton} ${selectedNetworks.includes(id) ? styles.socialButtonActive : ''
                      }`}
                      // onClick={() => toggleNetwork(id)}
                      title={id}
                      disabled={isScheduleReadOnly}
                    >
                      {Icon ? (
                        <Icon size={24} />
                      ) : (
                        <XTwitter width={24} height={24} color="currentColor" />
                      )}
                    </button>
                  ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
